import * as React from 'react';
import { StyleSheet } from 'aphrodite/no-important';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { useTheme } from '../../../theming/useTheme';
import { Quote } from '../../articleComponents/Quote';
import {
  Block,
  ImageBlock,
  QuoteBlock,
  HTMLBlock,
  JwBlock,
  GiphyBlock,
  ListItemTitleBlock,
  GenericBlock, InjectedHtmlBlock, IframeEmbedBlock,
  AdBlock, MMPlayerBlock,
} from './BlockTypes.utils';
import { Divider } from '../../articleComponents/Divider';
import { AdditionalStyleSheet } from '../../components.utils';
import { Iframe } from '../../articleComponents/media/Iframe';
import { Jw } from '../../articleComponents/media/Jw';
import { Paragraph } from '../../articleComponents/Paragraph';
import { Playbuzz } from '../../articleComponents/media/Playbuzz';
import { Streamable } from '../../articleComponents/media/Streamable';
import { Twitter } from '../../articleComponents/media/Twitter';
import { Giphy } from '../../articleComponents/media/Giphy';
import { Instagram } from '../../articleComponents/media/Instagram';
import { Facebook } from '../../articleComponents/media/Facebook';
import { ListItemTitle } from '../../articleComponents/ListItemTitle';
import { ImageWithFigcaption } from '../../partials/image/imageWithFigcaption/ImageWithFigcaption';
import { Image } from '../../partials/image/Image';
import { Ad } from '../../ads/Ad';
import { ViewabilityProvider } from '../../../viewability/viewabilityHandler';
import { InjectedBlock } from '../../articleComponents/media/InjectedBlock';
import { MMPlayer } from '../../articleComponents/media/MMPlayer';
import { IframeEmbed } from '../../articleComponents/media/IframeEmbed';
import { createImageWidths, getImageCaption } from '../../partials/image/image.utils';
import { BaseUnit } from '../../../theming/baseUnitDefinition';

export interface ArticleContentBaseProps {
  body: Array<Block>;
}

interface ArticleContentDataProps extends ArticleContentBaseProps {
  templateName: string;
  siteName: string;
}

interface ArticleContentThemeProps {
  baseUnit: BaseUnit;
}

const createBlockStyle = (baseUnit: BaseUnit, modifier: number) => StyleSheet.create({
  style: {
    marginTop: `${baseUnit * modifier}px`,
    marginLeft: `${baseUnit}px`,
    marginRight: `${baseUnit}px`,
  },
}) as AdditionalStyleSheet;

const imageWidths = createImageWidths(450, 600, 600);

const createBlocksComponents = (body: Array<Block>, baseUnit: BaseUnit, templateName: string, siteName: string) => {
  let isPreviousElementTitle = false;
  return body.map(block => {
    const { type } = block;
    const blockStyle = isPreviousElementTitle ? createBlockStyle(baseUnit, 0.5) : createBlockStyle(baseUnit, 1);
    isPreviousElementTitle = false;
    switch (type) {
      case 'ad': {
        const { dataId, adId } = block as AdBlock;
        return <Ad key={dataId} additionalStyle={blockStyle} id={adId} />;
      }
      case 'image': {
        const { image, dataId } = block as ImageBlock;
        const { credit, caption } = image;
        const figcaption = getImageCaption(credit, caption);
        return figcaption
          ? <ImageWithFigcaption key={dataId} {...image} figcaption={figcaption} lazyLoad additionalStyle={blockStyle} dataId={dataId} imageWidths={imageWidths} />
          : <Image key={dataId} {...image} lazyLoad additionalStyle={blockStyle} dataId={dataId} imageWidths={imageWidths} />;
      }
      case 'title': {
        isPreviousElementTitle = true;
        const { listItemNumber, text, dataId } = block as ListItemTitleBlock;
        return <ListItemTitle key={dataId} listItemNumber={listItemNumber} text={text} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'inline-text': {
        const { html, dataId } = block as HTMLBlock;
        return <Paragraph key={dataId} html={html} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'quote': {
        const { text, cite, dataId } = block as QuoteBlock;
        return <Quote key={dataId} text={text} cite={cite} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'youtube': {
        const { html, dataId } = block as HTMLBlock;
        return <Iframe key={dataId} html={html} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'twitter': {
        const { html, dataId } = block as HTMLBlock;
        return <Twitter key={dataId} html={html} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'instagram': {
        const { html, dataId } = block as HTMLBlock;
        return <Instagram key={dataId} html={html} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'facebook': {
        const { html, dataId } = block as HTMLBlock;
        return <Facebook key={dataId} html={html} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'jw': {
        const { html, videoId, dataId } = block as JwBlock;
        return <Jw key={dataId} html={html} videoId={videoId} templateName={templateName} siteName={siteName} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'mmPlayer': {
        const { html, dataId } = block as MMPlayerBlock;
        return <MMPlayer key={dataId} html={html} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'giphy': {
        const { dataId } = block as GenericBlock;
        return <Giphy key={dataId} {...block as GiphyBlock} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'playbuzz': {
        const { html, dataId } = block as HTMLBlock;
        return <Playbuzz key={dataId} html={html} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'dailyMotion': {
        const { html, dataId } = block as HTMLBlock;
        return <Iframe key={dataId} html={html} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'divider': {
        const { dataId } = block as GenericBlock;
        return <Divider key={dataId} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'streamable': {
        const { html, dataId } = block as HTMLBlock;
        return <Streamable key={dataId} html={html} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'injectedBlock': {
        const { html, dataId, script } = block as InjectedHtmlBlock;
        return <InjectedBlock key={dataId} html={html} additionalStyle={blockStyle} dataId={dataId} script={script} />;
      }
      case 'soundcloud': {
        const { html, dataId } = block as HTMLBlock;
        return <Iframe key={dataId} html={html} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'vimeo': {
        const { html, dataId } = block as HTMLBlock;
        return <Iframe key={dataId} html={html} additionalStyle={blockStyle} dataId={dataId} />;
      }
      case 'iframeEmbed': {
        const { html, dataId, height } = block as IframeEmbedBlock;
        return <IframeEmbed key={dataId} height={height} html={html} additionalStyle={blockStyle} dataId={dataId} />;
      }
      default: return null;
    }
  });
};

export const ArticleContentComponent: React.FunctionComponent<ArticleContentDataProps> = props => {
  const { body, templateName, siteName } = props;
  const { baseUnit } = useTheme<ArticleContentThemeProps>();
  return <ViewabilityProvider>{createBlocksComponents(body, baseUnit, templateName, siteName)}</ViewabilityProvider>;
};

export const ArticleContent = (props: ArticleContentDataProps) => <ErrorBoundary><ArticleContentComponent {...props} /></ErrorBoundary>;
